
import React, { useState, useEffect } from "react";
import Layout from "@/components/layout/Layout";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useGuide } from "@/hooks/use-guide";
import { GuidePreview } from "@/components/guides/GuidePreview";
import { supabase } from "@/integrations/supabase/client";
import { GuideDownloadForm } from "@/components/guides/GuideDownloadForm";
import { Session } from "@supabase/supabase-js";
import { GuideDownloadHandler } from "@/components/guides/GuideDownloadHandler";
import { GuideDetailHeader } from "@/components/guides/GuideDetailHeader";
import { GuideDetailContent } from "@/components/guides/GuideDetailContent";
import { GuideSidebar } from "@/components/guides/GuideSidebar";
import { Guide } from "@/types/guides";
import { BreadcrumbItemType } from "@/components/ui/breadcrumb-list";

export default function GuideDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { guide, isLoading, error } = useGuide(slug || "");
  const [showPreview, setShowPreview] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  
  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const openDownloadModal = queryParams.get('openDownloadModal') === 'true';
  
  // Fetch session on component mount
  useEffect(() => {
    async function getSession() {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
    }
    
    getSession();
    
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
      }
    );
    
    return () => subscription.unsubscribe();
  }, []);

  // Get download handling logic from our custom handler
  const { handleDownload, downloadFormGuide, setDownloadFormGuide } = GuideDownloadHandler({ session });

  // Open download modal if specified in URL and guide is loaded
  useEffect(() => {
    if (!isLoading && guide && openDownloadModal) {
      // Clean up URL without reloading the page
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Open the download modal
      setDownloadFormGuide(guide);
    }
  }, [guide, isLoading, openDownloadModal, setDownloadFormGuide]);

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-16 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-grandpal-green-dark"></div>
        </div>
      </Layout>
    );
  }

  if (error || !guide) {
    navigate("/guides");
    return null;
  }
  
  const breadcrumbItems: BreadcrumbItemType[] = [
    { label: "Home", href: "/" },
    { label: "Guides", href: "/guides" },
    { label: guide.title, href: `/guides/${guide.slug}` },
  ];

  return (
    <Layout>
      <Helmet>
        <title>{guide.title} | Grandpal</title>
      </Helmet>

      <div className="bg-gradient-to-b from-white to-grandpal-beige/20 py-16 md:py-24">
        <div className="container max-w-7xl">
          <GuideDetailHeader guide={guide} breadcrumbItems={breadcrumbItems} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <GuideDetailContent guide={guide} />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <GuideSidebar 
                guide={guide}
                onDownload={handleDownload}
                onPreview={() => setShowPreview(true)}
              />
            </div>
          </div>
        </div>
      </div>

      {showPreview && guide && (
        <GuidePreview 
          guide={guide} 
          onClose={() => setShowPreview(false)}
        />
      )}
      
      {downloadFormGuide && (
        <GuideDownloadForm
          guide={downloadFormGuide}
          open={!!downloadFormGuide}
          onOpenChange={(open) => !open && setDownloadFormGuide(null)}
        />
      )}
    </Layout>
  );
}
